import { Routes, Route } from 'react-router-dom';
import { AppProvider } from '@contexts/AppContext';
import { AuthProvider, useAuth } from '@contexts/AuthContext';
import { CartProvider } from '@contexts/CartContext';
import { CompareProvider } from '@contexts/CompareContext';
import { ContactModalProvider } from '@contexts/ContactModalContext';
import { FavouritesProvider } from '@contexts/FavouritesContext';

// Layout
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import Navigation from '@components/layout/Navigation';
import SecondaryNav from '@components/layout/SecondaryNav';

// Compare floating bar
import CompareBar from '@components/product/CompareBar';

// Pages
import HomePage from '@pages/HomePage';
import LoginPage from '@pages/LoginPage';
import CategoryPage from '@pages/CategoryPage';
import ProductListPage from '@pages/ProductListPage';
import ProductDetailPage from '@pages/ProductDetailPage';
import ComparePage from '@pages/ComparePage';
import SearchResultsPage from '@pages/SearchResultsPage';
import ProductFinderPage from '@pages/ProductFinderPage';
import ToolFinderPage from '@pages/ToolFinderPage';
import DSCConfiguratorPage from '@pages/DSCConfiguratorPage';
import FavouritesPage from '@pages/FavouritesPage';
import FavouriteDetailPage from '@pages/FavouriteDetailPage';
import CartPage from '@pages/CartPage';
import CheckoutPage from '@pages/CheckoutPage';
import OrdersPage from '@pages/OrdersPage';
import OrderDetailPage from '@pages/OrderDetailPage';
import ProfilePage from '@pages/ProfilePage';
import CasesPage from '@pages/CasesPage';
import DocumentsPage from '@pages/DocumentsPage';
import ContactPage from '@pages/ContactPage';
import InvoicesPage from '@pages/InvoicesPage';
import TrackDataPage from '@pages/TrackDataPage';
import ReportsPage from '@pages/ReportsPage';
import NotFoundPage from '@pages/NotFoundPage';

// Group F — Session & Consent
import ConsentBanner from '@components/common/ConsentBanner';
import StillThereModal from '@components/common/StillThereModal';
import OfflineBanner from '@components/common/OfflineBanner';
import useIdleTimer from '@hooks/useIdleTimer';

function AppShell() {
  const { isAuthenticated, logout } = useAuth();
  const { showWarning, countdown, stayActive } = useIdleTimer({
    enabled: isAuthenticated,
    onTimeout: () => logout(),
  });

  return (
    <>
      <div className="flex flex-col min-h-screen bg-white">
        <OfflineBanner />
        <Header />
        <Navigation />
        <SecondaryNav />
        <main className="flex-1">
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/product-finder" element={<ProductFinderPage />} />

            {/* Catalog */}
            <Route path="/products" element={<CategoryPage />} />
            <Route path="/products/:productLine" element={<CategoryPage />} />
            <Route path="/products/:productLine/:categorySlug" element={<CategoryPage />} />
            <Route path="/products/category/:subcategorySlug" element={<ProductListPage />} />
            <Route path="/products/detail/:itemCode" element={<ProductDetailPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/compare" element={<ComparePage />} />

            {/* ToolFinder */}
            <Route path="/tool-finder" element={<ToolFinderPage />} />
            <Route path="/tool-finder/results" element={<ToolFinderPage />} />
            <Route path="/toolfinder" element={<ToolFinderPage />} />
            <Route path="/toolfinder/results" element={<ToolFinderPage />} />
            <Route path="/drill-string-configurator" element={<DSCConfiguratorPage />} />
            <Route path="/drill-string-configurator/:type" element={<DSCConfiguratorPage />} />

            {/* Account */}
            <Route path="/favourites" element={<FavouritesPage />} />
            <Route path="/favourites/:listId" element={<FavouriteDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:orderId" element={<OrderDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/my-track-data" element={<TrackDataPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <CompareBar />
      <ConsentBanner />
      <StillThereModal
        isOpen={showWarning}
        countdown={countdown}
        onStay={stayActive}
        onLogout={logout}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <CartProvider>
          <CompareProvider>
            <ContactModalProvider>
              <FavouritesProvider>
                <AppShell />
              </FavouritesProvider>
            </ContactModalProvider>
          </CompareProvider>
        </CartProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
